import { now, subtract } from '@eturi/date-util'
import { useT } from '@eturi/react-i18next'
import type { PickDefined, Simplify, Writable } from '@eturi/ts-helpers'
import type { FieldValidator } from 'final-form'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { tError } from '../../widgets'
import type { SelectFieldProps } from './SelectField'
import { useBirthdayDisplayAge } from './useBirthdayDisplayAge'

export type UseChildAge = ReturnType<typeof useChildAgeField>

export type UseChildAgeField = {
	readonly birthdayToAge: (birthday: string) => number
	readonly props: Simplify<
		Readonly<
			PickDefined<SelectFieldProps, 'emptyValue' | 'initialValue' | 'name' | 'options'> &
				Pick<SelectFieldProps, 'label' | 'validate'>
		>
	>

	readonly serverValue: (birthday: Maybe<string>) => Maybe<string>
}

/**
 * Hook to help construct a Birthday picker.
 */
export const useChildAgeField = (
	initialBirthday: Maybe<string>,
	label?: boolean,
	validate?: boolean,
): UseChildAgeField => {
	const [t] = useT()
	const displayAge = useBirthdayDisplayAge()
	const normalizedInitialBirthday = initialBirthday || BIRTHDAY_DEFAULT

	return useMemo((): UseChildAgeField => {
		const initialAge = parseBirthdayToAge(normalizedInitialBirthday)

		const birthdayOptions = Array.from({ length: 19 }, (_, age) => {
			// Make sure not to change the birthday if the age doesn't change
			const value =
				age === 0 ? BIRTHDAY_DEFAULT
				: age === initialAge ? normalizedInitialBirthday
				: parseAgeToBirthday(age)

			const label = displayAge(age)

			return { label, value }
		})

		const fieldLabel = t('form_controls.age')

		const fieldProps: Writable<UseChildAgeField['props']> = {
			emptyValue: BIRTHDAY_DEFAULT,
			options: birthdayOptions,
			initialValue: normalizedInitialBirthday,
			name: 'birthday',
		}

		if (label) fieldProps.label = fieldLabel

		if (validate) {
			fieldProps.validate = ((v: Maybe<string>) =>
				v && v !== BIRTHDAY_DEFAULT ?
					undefined
				:	tError(t('error_strings.field_required', { field: fieldLabel }))) satisfies FieldValidator<
				Maybe<string>
			>
		}

		return {
			birthdayToAge: parseBirthdayToAge,
			props: fieldProps,
			serverValue: (v: string) => (v === BIRTHDAY_DEFAULT ? null : v),
		}
	}, [t, displayAge, label, normalizedInitialBirthday, validate])
}

const BIRTHDAY_DEFAULT = 'N/A'
// NOTE: We aren't using a standard localized format b/c birthday isn't
//  displayed We actually only display age. The fact that we have a field
//  "birthday" that we try to calculate based on "age" is really silly.
const BIRTHDAY_FORMAT = 'YYYY-MM-DD'

const parseAgeToBirthday = (age: number) => {
	const date = subtract(now.subtract(age || 0, 'y'), 1, 'd')
	const MM = `${date.getMonth() + 1}`.padStart(2, '0')
	const DD = `${date.getDate()}`.padStart(2, '0')

	return `${date.getFullYear()}-${MM}-${DD}`
}

const parseBirthdayToAge = (birthday: string) =>
	birthday && birthday != BIRTHDAY_DEFAULT ?
		new Date().getFullYear() - moment(birthday, BIRTHDAY_FORMAT).year()
	:	0
